import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import ShowMoreModal from "../../../common/components/ShowMoreModal";

// components
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import { getFullName } from "../../../reports-settings/report-type/helpers/form.handler";

//helpers and types
import { all_routes } from "../../../../router/all_routes";
import {
  getDaysUntilDue,
  getPriorityConfig,
  calculatePriority,
} from "../helpers/upcomingReports.helper";

import { ReportType, Priority } from "../types";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";

interface UpcomingReportCardProps {
  report: ReportType;
  upcomingReportType?: any[];
  allUsers?: any[];
}

const UpcomingReportCard: React.FC<UpcomingReportCardProps> = ({
  report,
  upcomingReportType,
  allUsers,
}) => {
  // STATES
  const [isModalOpen, setIsModalOpen] = useState(false);

  const priority: Priority = calculatePriority(report.due_date);
  const daysUntilDue = getDaysUntilDue(report.due_date);
  const priorityConfig = getPriorityConfig(priority);

  // visible to list
  const showMaxItems = 1;
  const displayNames = report?.visible_to.slice(0, showMaxItems);
  const hasMore = report?.visible_to.length > showMaxItems;
  const modalDisplayNames = report?.visible_to.slice(showMaxItems);

  const handleShowMore = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Don't render if no due date
  if (!report.due_date) {
    return null;
  }

  const getFrequencyDisplayText = (): string => {
    const { frequency_type, weekly_interval, monthly_interval } = report;

    switch (frequency_type) {
      case "daily":
        return "Daily";
      case "weekly":
        return weekly_interval === 1
          ? "Weekly"
          : `Every ${weekly_interval} weeks`;
      case "monthly":
        return monthly_interval === 1
          ? "Monthly"
          : `Every ${monthly_interval} months`;
      default:
        return frequency_type.charAt(0).toUpperCase() + frequency_type.slice(1);
    }
  };

  return (
    <>
      <div key={report.id} className="card border-0 mb-0 p-3 shadow-sm">
        <div className="card-body px-3 py-2 bg-white">
          <div className="d-flex align-items-start justify-content-between mb-2">
            <h6 className="report-name mb-0 flex-grow-1 me-2 text-dark fw-semibold">
              {report.name}
            </h6>
            <span
              className={`badge bg-${priorityConfig.color} badge-sm d-flex align-items-center`}>
              <i className={`ti ${priorityConfig.icon} me-1`}></i>
              {daysUntilDue}
            </span>
          </div>

          {/* Meta Information */}
          <div className="report-meta mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <small className="text-muted d-flex align-items-center">
                <i className="ti ti-calendar-due me-1 text-info"></i>
                Due: {humanReadableDate(report.due_date)}
              </small>
            </div>

            {/* Reports To Row */}
            <div className="d-flex align-items-center mb-1">
              <small className="text-muted d-flex align-items-center">
                <i className="ti ti-user text-info me-1"></i>
                Visible to:<span className="me-1"></span>
              </small>
              {report.visible_to.length == 0 ? (
                <small className="text-primary px-2">No records to show!</small>
              ) : (
                <>
                  {displayNames.map((item: any) => (
                    <div
                      key={item?.id}
                      className="px-1 assignment-item mb-1 d-flex align-items-center">
                      <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
                        <ImageWithBasePath
                          src={
                            !item?.profile_pic || item?.profile_pic === "null"
                              ? "assets/img/users/user.png"
                              : item?.profile_pic
                          }
                          alt="img"
                        />
                      </li>

                      <span className={`text-muted me-1`}>
                        {getFullName(
                          item?.salutation,
                          item?.fname,
                          item?.lname
                        )}
                      </span>
                    </div>
                  ))}
                  {hasMore && (
                    <Link
                      to="#"
                      className="px-2 btn-link text-decoration-none"
                      onClick={handleShowMore}>
                      <small className="text-primary">
                        +{report?.visible_to.length - showMaxItems} more
                      </small>
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Action Button */}
          <div className="d-flex w-100 align-items-center justify-content-center gap-3">
            <small className="badge badge-md badge-secondary-transparent d-flex align-items-center">
              <i className="ti ti-repeat me-1"></i>
              {getFrequencyDisplayText()}
            </small>
            <Link
              to={`${all_routes.ersReportsCreate}`}
              className={`btn btn-${priorityConfig.color} btn-sm w-auto d-flex align-items-center justify-content-center`}
              state={{
                reportType: report,
                upcomingReportType: upcomingReportType,
                allUsers: allUsers,
                disableReportTypeChange: true,
              }}>
              <i className="ti ti-file-plus me-2"></i>
              Start Report
            </Link>
          </div>
        </div>
      </div>

      {/* Custom Modal - Only renders when needed */}
      <ShowMoreModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        items={modalDisplayNames}
      />
    </>
  );
};

export default UpcomingReportCard;
